// @ts-nocheck
// Ported byte-identically from v1 router/context/BreakContext.js.
// Owns the agent's break lifecycle: fetching the break catalogue,
// starting/stopping a break, restoring an in-progress break on reload
// (server API_USER_BREAKS_ACTIVE first, then Storage.activeBreak fallback),
// and driving the countdown that flips into "exceeded" when the timer
// runs out. Consumed by CTIAgentUI and BreakStatusCapsule.
import { createContext, useState, useEffect, useCallback } from 'react';

import { postApiCall, getApiCall } from '@/service/httpCall';
import {
  API_BREAK_LIST,
  USER_BREAKS_EDIT,
  USER_BREAKS_CREATE,
  USER_BREAKS_ACTIVE,
} from '@/service/url';
import { toast } from 'sonner';
import { Storage } from '@/utilities/setting';
import { trackActivity } from '@/service/activityTracker';

// Create Context
export const BreakContext = createContext(undefined as any);

export const BreakProvider = ({ children }) => {
  const [userStatus, setUserStatus] = useState('AVAILABLE');
  const [dotColor, setDotColor] = useState('#28a745');

  const [breakExceeded, setBreakExceeded] = useState(false);
  const [breakEndTime, setBreakEndTime] = useState<any>(null);
  const [breakStartTime, setBreakStartTime] = useState<any>(null);
  const [countdown, setCountdown] = useState<any>(null);

  const [selectedBreakId, setSelectedBreakId] = useState<any>(null);
  const [breaksData, setBreaksData] = useState<any[]>([]);
  const activeBreak = breaksData.find((b) => b.id == selectedBreakId) || null;

  useEffect(() => {
    if (userStatus !== 'BREAK' || !breakEndTime) return;

    const timer = setInterval(() => {
      const remaining = Math.floor((breakEndTime - Date.now()) / 1000);

      if (remaining <= 0) {
        setCountdown(0);
        setBreakExceeded(true);
        clearInterval(timer);
      } else {
        setCountdown(remaining);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [userStatus, breakEndTime]);

  useEffect(() => {
    checkAndRestoreBreak();
  }, []);

  const applyRestoredBreak = ({ breakId, startTime, endTime }) => {
    const remainingSeconds = Math.floor((endTime - Date.now()) / 1000);
    setUserStatus('BREAK');
    setSelectedBreakId(breakId);
    setBreakEndTime(endTime);
    setBreakStartTime(startTime || null);
    if (remainingSeconds > 0) {
      setCountdown(remainingSeconds);
      setBreakExceeded(false);
    } else {
      setCountdown(0);
      setBreakExceeded(true);
    }
  };

  const checkAndRestoreBreak = async () => {
    if (!Storage.getItem('access_token')) {
      return;
    }

    let user = null;
    try {
      user = JSON.parse(Storage.getItem('user') || 'null');
    } catch (e) {
      user = null;
    }

    try {
      const active: any = await getApiCall(USER_BREAKS_ACTIVE);
      if (active && active.break) {
        const durationMin = Number(active.break.duration || 0);
        const totalSeconds = Number(
          active.total_seconds != null ? active.total_seconds : durationMin * 60
        );
        const remainingSeconds = Number(
          active.remaining_seconds != null ? active.remaining_seconds : totalSeconds
        );
        const elapsedSeconds = Number(
          active.elapsed_seconds != null
            ? active.elapsed_seconds
            : Math.max(0, totalSeconds - remainingSeconds)
        );

        if (Number.isFinite(remainingSeconds)) {
          const now = Date.now();
          const startTime = now - elapsedSeconds * 1000;
          const endTime = now + remainingSeconds * 1000;
          applyRestoredBreak({ breakId: active.break.id, startTime, endTime });
          Storage.setItem(
            'activeBreak',
            JSON.stringify([
              {
                breakData: { ...active.break, user_id: user?.id },
                endTime,
                startTime,
                user_id: user?.id,
              },
            ])
          );
          return;
        }
      } else if (active === null || active === undefined) {
        Storage.removeItem('activeBreak');
        return;
      }
    } catch (e) {
      console.warn('Active break fetch failed, falling back to local copy:', e);
    }

    try {
      const breakDataStorage = Storage.getItem('activeBreak');
      if (!breakDataStorage) return;

      const breakArray = JSON.parse(breakDataStorage);
      const userBreak = breakArray.find((b) => b.user_id === user?.id);
      if (!userBreak) return;

      const { breakData, endTime, startTime } = userBreak;
      applyRestoredBreak({ breakId: breakData.id, startTime, endTime });
    } catch (e) {
      console.error('Error restoring break state:', e);
      setUserStatus('AVAILABLE');
    }
  };

  const durationToSeconds = (duration) => {
    const minutes = parseInt(duration, 10);
    return minutes * 60;
  };

  const fetchBreaks = useCallback(async () => {
    const resp: any = await getApiCall(`${API_BREAK_LIST}?status=1`, {});
    const list = Array.isArray(resp) ? resp : [];
    setBreaksData(list);
    return list;
  }, []);

  const startBreak = async (selectedBreakId) => {
    if (!selectedBreakId) {
      toast.error('Please select a break');
      return;
    }

    try {
      let selectedBreak = breaksData.find((b) => b.id == selectedBreakId);
      if (!selectedBreak) {
        const fresh = await fetchBreaks();
        selectedBreak = (fresh || []).find((b) => b.id == selectedBreakId);
      }

      if (!selectedBreak || selectedBreak.duration == null) {
        toast.error('Could not determine break duration. Refresh and retry.');
        return;
      }

      const resp: any = await postApiCall(USER_BREAKS_CREATE, {
        reason: selectedBreakId,
      });

      const seconds = durationToSeconds(Number(selectedBreak.duration));

      const startTime = Date.now();
      const endTime = startTime + seconds * 1000;

      const newBreak = {
        breakData: resp?.userBreaks,
        endTime,
        startTime,
        user_id: resp?.userBreaks?.user_id,
      };

      Storage.setItem('activeBreak', JSON.stringify([newBreak]));

      setBreakStartTime(startTime);
      setBreakEndTime(endTime);
      setCountdown(seconds);
      setUserStatus('BREAK');
      setBreakExceeded(false);

      trackActivity('break_start', {
        break_id: selectedBreak?.id ?? selectedBreakId,
        break_title: selectedBreak?.title || null,
        duration_minutes: Number(selectedBreak?.duration) || null,
        end_time: new Date(endTime).toISOString(),
      });

      toast.success('Break Applied');
    } catch (error) {
      console.error(error);
      toast.error(typeof error === 'string' && error ? error : 'Error while applying break');
    }
  };

  const stopBreak = async (userId) => {
    if (!userId) {
      toast.error('User Id Not Found');
      return;
    }

    try {
      const url = USER_BREAKS_EDIT.replace(':id', userId);
      await postApiCall(url);

      Storage.removeItem('activeBreak');

      const now = Date.now();
      const durationMinutes = Number(activeBreak?.duration) || null;
      const totalSeconds = durationMinutes != null ? durationMinutes * 60 : null;
      const remainingSeconds = breakEndTime
        ? Math.max(0, Math.round((breakEndTime - now) / 1000))
        : null;
      const onBreakSeconds = breakStartTime
        ? Math.max(0, Math.round((now - breakStartTime) / 1000))
        : totalSeconds != null && remainingSeconds != null
          ? Math.max(0, totalSeconds - remainingSeconds)
          : null;

      const toMinutes = (s) => (s == null ? null : Math.round((s / 60) * 10) / 10);

      setUserStatus('AVAILABLE');
      setBreakEndTime(null);
      setBreakStartTime(null);
      setCountdown(null);
      setBreakExceeded(false);

      trackActivity('break_stop', {
        break_id: selectedBreakId,
        break_title: activeBreak?.title || null,
        duration_minutes: durationMinutes,
        time_on_break_minutes: toMinutes(onBreakSeconds),
        time_remaining_minutes: toMinutes(remainingSeconds),
        ended_early: remainingSeconds != null ? remainingSeconds > 0 : null,
      });

      setSelectedBreakId(null);

      toast.success('Break cancelled');
    } catch (error) {
      console.error(error);
      toast.error('Error while cancelling break');
    }
  };

  useEffect(() => {
    if (userStatus === 'AVAILABLE') {
      setDotColor('#28a745');
    } else if (userStatus === 'BREAK') {
      setDotColor(breakExceeded ? '#dc3545' : '#ffc107');
    } else {
      setDotColor('#6c757d');
    }
  }, [userStatus, breakExceeded]);

  return (
    <BreakContext.Provider
      value={{
        // state
        userStatus,
        dotColor,
        countdown,
        breakExceeded,
        selectedBreakId,
        breaksData,
        activeBreak,
        breakEndTime,
        breakStartTime,

        // actions
        startBreak,
        stopBreak,
        setSelectedBreakId,
        fetchBreaks,
      }}
    >
      {children}
    </BreakContext.Provider>
  );
};
